import { useEffect, useRef, useState } from "react"
import Card from "@/components/card"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { Document } from "../model/document.types"
import { DocumentYearGroup } from "../utils/document-year.utils"
import DocumentList from "./DocumentList"
import { motion, useReducedMotion } from "framer-motion"

interface Props {
  group: DocumentYearGroup
  isPastYear: boolean
  latestDuerpId?: string
  latestPapripactId?: string
  autoOpenType?: Document["type"]
  isDuerpOutdated: boolean
  isOpen: boolean
  onToggle: () => void
}

export default function DocumentYearCard({
  group,
  isPastYear,
  latestDuerpId,
  latestPapripactId,
  autoOpenType,
  isDuerpOutdated,
  isOpen,
  onToggle
}: Props) {
  const [isAnimating, setIsAnimating] = useState(false)
  const reduceMotion = useReducedMotion()
  const contentRef = useRef<HTMLDivElement>(null)
  // Current versions first (DUERP then PAPRIPACT), then older versions by date
  const currentDuerp = group.duerps.find((document) => document.id === latestDuerpId)
  const currentPapripact = group.papripacts.find((document) => document.id === latestPapripactId)
  const currentDocuments = [currentDuerp, currentPapripact].filter((document): document is Document =>
    Boolean(document)
  )
  const documents = [
    ...currentDocuments,
    ...[...group.duerps, ...group.papripacts]
      .filter((document) => !currentDocuments.includes(document))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
  ]

  // Set inert in an effect: motion.div memoizes its ref, so an inline ref callback only runs on mount
  useEffect(() => {
    if (contentRef.current) contentRef.current.inert = !isOpen
  }, [isOpen])

  return (
    <Card isWithoutPadding>
      <button
        type="button"
        className="_hover flex w-full min-h-[64px] items-center gap-[12px] rounded-[12px] bg-transparent px-[30px] py-[16px] text-left"
        style={{ border: "none" }}
        aria-expanded={isOpen}
        aria-controls={`documents-${group.year}`}
        onClick={onToggle}>
        <span className="flex h-[24px] w-[16px] shrink-0 items-center justify-center">
          <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} className="grey-t block" />
        </span>
        <span className="text-lg font-normal leading-[24px]">{group.year}</span>
      </button>
      <motion.div
        id={`documents-${group.year}`}
        ref={contentRef}
        aria-hidden={!isOpen}
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.22, ease: "easeInOut" }}
        onAnimationStart={() => setIsAnimating(true)}
        onAnimationComplete={() => setIsAnimating(false)}
        style={{ overflow: isAnimating || !isOpen ? "hidden" : "visible" }}>
        <div style={{ borderTop: "1px solid #eeeeee" }}>
          <DocumentList
            documents={documents}
            currentDocumentIds={currentDocuments.map((document) => document.id)}
            latestDuerpId={latestDuerpId}
            showDuerpWarning={isDuerpOutdated}
            isDuerpMissing={!isPastYear && group.duerps.length === 0}
            autoOpenDocumentId={
              autoOpenType === "duerp"
                ? currentDuerp?.id
                : autoOpenType === "papripact"
                  ? currentPapripact?.id
                  : undefined
            }
          />
        </div>
      </motion.div>
    </Card>
  )
}
